import { LoadingOutlined } from '@ant-design/icons'
import { Horizontal, RouteMap, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Drawer, Spin } from 'antd'
import React, { type MutableRefObject, useRef } from 'react'

import { DetailsLineItemsGrid } from '../../Invoices/Detail/components/DetailsLineItemsGrid'
import { InvoiceDropSummary, OrderAttachments, OrderHeader, RouteLogTimeline, StatsCards, StatusBanner } from './index'
import { useOrderFreightData, useOrderNavigation } from '../hooks'
import type { OrderFreightDrawerProps } from '../types'
import { OrderNavigationHeader } from './OrderNavigationHeader'
import { TransportInfo } from './TransportInfo'
import type { GridApi } from 'ag-grid-community'
import type { InvoiceDetail, InvoiceTransaction, TransactionRow } from '../../../../api/useFreight/types'
import { getRouteStops } from '../utils'

/**
 * A drawer component that displays detailed freight information for an order
 */
export function OrderFreightDrawer({
  open,
  onClose,
  order,
  allOrders = [],
  currentIndex = -1,
  onNavigate,
  costRevenue,
}: OrderFreightDrawerProps): JSX.Element {
  // Create a ref for the freight lines grid
  const freightLinesGridRef = useRef<GridApi<InvoiceDetail> | null>(null)

  // Extract order data for hook props
  const orderId = order?.order_id
  const orderNumber = order?.order_number
  const invoiceGroupNumber = order && 'invoice_group_number' in order ? order.invoice_group_number : null

  // Use our custom hooks
  const {
    orderExtraData,
    isLoadingOrder,
    routeLogData,
    isLoadingRoute,
    transactionDetails,
    transactionsLoading,
    invoiceId,
  } = useOrderFreightData({
    orderId,
    orderNumber: orderNumber as string,
    costRevenue,
    invoiceGroupNumber: invoiceGroupNumber as string | null,
  })

  const { handlePrevious, handleNext } = useOrderNavigation({
    open,
    currentIndex,
    totalOrders: allOrders.length,
    onNavigate,
  })
  return (
    <Drawer
      placement='bottom'
      open={open}
      onClose={onClose}
      height='98vh'
      styles={{
        body: { padding: 0, display: 'flex' },
        header: { padding: '0 .5em' },
      }}
      title={
        <OrderNavigationHeader
          currentIndex={currentIndex}
          totalOrders={allOrders.length}
          onPrevious={handlePrevious}
          onNext={handleNext}
        />
      }
    >
      {isLoadingRoute || isLoadingOrder ? (
        <Vertical flex={3} horizontalCenter verticalCenter>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </Vertical>
      ) : (
        <Horizontal
          flex={1}
          gap={8}
          style={{
            padding: '8px',
            backgroundColor: 'var(--bg-1)',
            overflow: 'auto',
          }}
        >
          {/* Left Card */}
          <Vertical gap={16} flex={1}>
            <OrderHeader order={order} invoiceId={invoiceId} />

            <TransportInfo
              driverName={orderExtraData?.carrier || orderExtraData?.assigned_driver || 'Unknown'}
              routeLogData={routeLogData}
            />
            <OrderAttachments orderNumber={order.order_number} />
            <RouteLogTimeline routeLog={routeLogData} />
          </Vertical>

          <Vertical flex={3} bg='elevated' className='p-3' height={'auto'}>
            <Vertical flex={3} gap={8} style={{ overflow: 'none' }}>
              <Horizontal verticalCenter className='mb-1'>
                <StatsCards routeLog={routeLogData} />
              </Horizontal>
              <Horizontal>
                <Vertical border='top' style={{ minHeight: '300px' }}>
                  <RouteMap stops={getRouteStops(routeLogData)} />
                </Vertical>

                {routeLogData && <InvoiceDropSummary routeLogData={routeLogData} />}
              </Horizontal>
            </Vertical>
            <StatusBanner details={order?.details || []} />
            {/* Details Line Items Grid */}

            <Vertical className='mt-2'>
              {transactionDetails ? (
                <DetailsLineItemsGrid
                  filteredFreightLines={transactionDetails || []}
                  freightLinesGridRef={freightLinesGridRef as MutableRefObject<GridApi<InvoiceDetail>>}
                  showAddAccessorial
                  singleOrder={order as TransactionRow | InvoiceTransaction}
                  key='order-freight-lines'
                />
              ) : (
                <Vertical flex={3} horizontalCenter verticalCenter>
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                </Vertical>
              )}
            </Vertical>
          </Vertical>
        </Horizontal>
      )}
    </Drawer>
  )
}
